import {
  useDeleteTicketMutation,
  useGetTicketsQuery,
  usePermanentDeleteTicketMutation,
  useRestoreTicketMutation,
  useUpdateTicketMutation,
  downloadTicketsPdf,
} from "../../api/ticketApi/ticket";
import { Badge, Button, Card, Popover, Spinner } from "flowbite-react";
import TicketTable from "../../components/Table";
import { QueryParams, Ticket } from "@/sharedTypes";
import { useNavigate } from "react-router";
import TicketFormDialog from "../TicketFormDialog"; // Ensure TicketFormDialog is imported
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import TicketFormFilter from "../../components/TicketFormFilter";
import { HiOutlineEye, HiPencilAlt, HiTrash, HiDownload } from "react-icons/hi";
import { FaFilter } from "react-icons/fa";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import UseErrorHandler from "@/components/UseErrorHandler";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import { useTranslation } from "react-i18next";

export default function TicketManagement() {
  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: { is_active: true },
    pagination: {
      pageSize: 15,
      page: 1,
    },
  });

  const {
    data,
    isError,
    error,
    isLoading: isFetching,
  } = useGetTicketsQuery(queryParams, {
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  const { t } = useTranslation("ticket");

  UseErrorHandler({
    title: t("ticketManagement.failedToFetchTickets"),
    isError,
    error,
  });

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [ticketToUpdate, setTicketToUpdate] = useState<number | undefined>(
    undefined,
  );
  const [editFallbacks, setEditFallbacks] = useState<{
    categoryName?: string;
    subcategoryName?: string;
    clientName?: string;
    agentName?: string;
    updateReasonName?: string;
  }>({});

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [confirmationDialog, setConfirmationDialog] = useState<{
    title: string;
    body: string;
    onCancel: () => void;
    onConfirm: () => void;
    isLoading: boolean;
  } | null>(null);

  const navigate = useNavigate();
  // Add react hook form methods
  const methods = useForm();
  const [showInactive, setShowInactive] = useState(false);

  const handleEdit = (ticket: any) => {
    const ticktFormFields = {
      ticket_title: ticket.ticket_title,
      ticket_description: ticket.ticket_description,
      last_priority_id: ticket.last_priority_id,
      last_category_id: ticket.last_category_id || null,
      last_subcategory_id: ticket.last_subcategory_id || null,
      last_assigned_agent_id: ticket.last_assigned_agent_id || null,
      update_reason_id: ticket.update_reason_id || null,
      last_status_id: ticket.last_status_id || null,
      client_id: ticket.client_id || null,
    };

    setTicketToUpdate(ticket?.ticket_id);
    setEditFallbacks({
      categoryName:
        typeof ticket.category === "string"
          ? ticket.category
          : ticket.category?.category_name,
      subcategoryName:
        typeof ticket.subcategory === "string"
          ? ticket.subcategory
          : ticket.subcategory?.subcategory_name,
      clientName:
        ticket.client?.client_displayname ||
        ticket.client?.client_username ||
        ticket.client_name,
      agentName:
        ticket.last_assigned_agent?.agent_username || ticket.assigned_agent,
      updateReasonName:
        ticket.update_reason?.update_reason_description,
    });

    methods.reset(ticktFormFields);
    setIsFormDialogOpen(true);
  };

  const [updateTicket, { isLoading }] = useUpdateTicketMutation();

  const handleUpdate = async (data: Ticket) => {
    const clientId = typeof data.client_id === "number"
    ? data.client_id
    : Number(String(data.client_id).trim());

    if (!Number.isFinite(clientId) || clientId <= 0) {
      toast.error(t("ticketManagement.toast.invalidCredentialError"));
      return;
    }

    console.log("Updating ticket with data:", { ...data, client_id: clientId, ticketId: ticketToUpdate });
    
    updateTicket({ ...data, client_id: clientId, ticketId: ticketToUpdate as number })
      .unwrap()
      .then(() => {
        toast.success(t("ticketManagement.toast.updatedSuccess"));
        methods.reset();
        setIsFormDialogOpen(false);
      })
      .catch((error: any) => {
        toast.error(`${getErrorMessage(error)}`);
      });
  };

  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false);
    setTicketToUpdate(undefined);
    setEditFallbacks({});
  };

  //Filtering logic
  const filterMethods = useForm<any>({
    defaultValues: {
      ticket_title: "",
      ticket_description: "",
      last_priority_id: "",
      last_category_id: "",
      last_subcategory_id: null,
      client_id: "",
      client_group_id: "",
      last_assigned_agent_id: "",
      startDate: "",
      endDate: "",
      is_active: showInactive ? false : true,
    },
  });

  const handleFilterApply = (appliedFilters: Ticket) => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...appliedFilters,
        is_active: !showInactive,
      },
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to the first page
      },
    }));
    setIsFilterOpen(false);
  };

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        is_active: !showInactive,
      },
    }));
  }, [showInactive]);

  //Permanant deletion
  const [permanentDeleteTicket, { isLoading: deletingPermanently }] =
    usePermanentDeleteTicketMutation();

  const handlePermanentDelete = (ticketId: string) => {
    setConfirmationDialog({
      title: t("ticketManagement.permanentDeleteConfirmDialog.title"),
      body: t("ticketManagement.permanentDeleteConfirmDialog.message"),
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        permanentDeleteTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success(t("ticketManagement.toast.permanentDeleteSuccess"));
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: deletingPermanently,
    });
  };

  //Restoration
  const [restoreTicket, { isLoading: isRestoring }] =
    useRestoreTicketMutation();

  const handleRestore = (ticketId: string) => {
    setConfirmationDialog({
       title: t("ticketManagement.restoreConfirmDialog.title"),
      body: t("ticketManagement.restoreConfirmDialog.message"),
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        restoreTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success(t("ticketManagement.toast.restoreSuccess"));
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: isRestoring,
    });
  };

  //Soft deletion
  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();

  const handleDelete = (ticketId: string) => {
    setConfirmationDialog({
      title: t("ticketManagement.deleteConfirmDialog.title"),
      body: t("ticketManagement.deleteConfirmDialog.message"),
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        deleteTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success(t("ticketManagement.toast.deleteSuccess"));
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: isDeleting,
    });
  };

  const priorityConfig: Record<string, { bg: string; text: string }> = {
    low: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
    medium: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400" },
    high: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" },
    urgent: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
    critical: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
  };

  const channelConfig: Record<number, { bg: string; text: string; label: string }> = {
    0: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", label: t("ticketManagement.platformChannel") },
    1: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", label: t("ticketManagement.emailChannel") },
    2: { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400", label: t("ticketManagement.aiWidgetChannel") },
  };

  const columns = [
    { key: "ticket_ref", label: t("ticketManagement.columns.id") },
    {
      key: "ticket_title",
      label: t("ticketManagement.columns.title"),
      render: (row: any) => (
        <span className="font-medium text-gray-900 dark:text-white">{row.ticket_title}</span>
      ),
    },
    { key: "status", label: t("ticketManagement.columns.status") },
    {
      key: "priority",
      label: t("ticketManagement.columns.priority"),
      render: (row: any) => {
        const key = row.priority?.toLowerCase();
        const config = priorityConfig[key] || { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-700 dark:text-gray-300" };
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
            {row.priority}
          </span>
        );
      },
    },
    { key: "category", label: t("ticketManagement.columns.category") },
    { key: "subcategory", label: t("ticketManagement.columns.subCategory") },
    { key: "createdAt", label: t("ticketManagement.columns.createdAt") },
    { key: "resolvedAt", label: t("ticketManagement.columns.resolvedAt") },
    {
      key: "client_name",
      label: t("ticketManagement.columns.client"),
      render: (row: any) => (
        <div className="leading-tight">
          <div className="font-medium text-gray-900 dark:text-white">{row.client_name}</div>
          {row.client_email && (
            <div className="text-xs text-gray-400 dark:text-gray-500">{row.client_email}</div>
          )}
        </div>
      ),
    },
    { key: "client_group_name", label: t("ticketManagement.columns.clientGroup") },
    { key: "assigned_agent", label: t("ticketManagement.columns.assignedTo") },
    {
      key: "channel",
      label: t("ticketManagement.columns.channel"),
      render: (row: any) => {
        const channel = Number(row.channel);
        const config = channelConfig[channel] || { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-600 dark:text-gray-400", label: t("ticketManagement.otherChannel") };
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: t("ticketManagement.columns.actions"),
      render: (row: any) => (
        <div className="flex items-center gap-1">
          {showInactive ? (
            <>
              <button
                onClick={() => handleRestore(row.ticket_id)}
                title={t("ticketManagement.actions.restore")}
                className="rounded-lg p-1.5 text-green-600 transition-colors hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
              >
                <MdOutlineSettingsBackupRestore className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePermanentDelete(row.ticket_id)}
                title={t("ticketManagement.actions.permanentDelete")}
                className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                <HiTrash className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate(`/tickets/${row.ticket_id}`)}
                title={t("ticketManagement.actions.view")}
                className="rounded-lg p-1.5 text-blue-600 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30"
              >
                <HiOutlineEye className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleEdit(row)}
                title={t("ticketManagement.actions.edit")}
                className="rounded-lg p-1.5 text-purple-600 transition-colors hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-900/30"
              >
                <HiPencilAlt className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(row.ticket_id)}
                title={t("ticketManagement.actions.delete")}
                className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                <HiTrash className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const formattedData =
    data?.items.map((ticket: any) => ({
      ...ticket,
      ticket_id: ticket.ticket_id, // Include ticket_id for the View action
      ticket_title: ticket.ticket_title,
      ticket_description: ticket.ticket_description,
      status: ticket.status.status_name,
      priority: ticket.priority.priority_name,
      createdAt: new Date(ticket.createdAt).toLocaleString(),
      resolvedAt: ticket.closed_at ? new Date(ticket.closed_at).toLocaleString() : "—",
      category: ticket.category?.category_name || t("ticketManagement.notAvailable"),
      subcategory: ticket.subcategory?.subcategory_name || t("ticketManagement.notAvailable"),
      client_name: ticket.client?.client_username || t("ticketManagement.notAvailable"),
      client_email: ticket.client?.client_email || "",
      client_group_name: ticket.client?.client_group?.group_name || t("ticketManagement.notAvailable"),
      assigned_agent:
        ticket.last_assigned_agent?.agent_username || t("ticketManagement.unassigned"),
      channel: ticket.channel || "",
      })) || [];

      console.log("Formatted Data:", formattedData);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      const blob = await downloadTicketsPdf(queryParams.filters || {});
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tickets-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("ticketManagement.toast.pdfDownloadSuccess"));
    } catch (error: any) {
      toast.error(t("ticketManagement.toast.pdfDownloadFailed"));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePaginationChange = (newPageIndex: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page: newPageIndex,
      },
    }));
  };

  return (
    <>
      <Card className="overflow-x-auto">
        <div>
          {isFetching ? (
            <div className="flex w-full items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <p className="text-red-500">{t("ticketManagement.failedToLoadTickets")}</p>
          ) : (
            <div>
              <div className="mb-4 flex items-center gap-3">
              <Popover
                open={isFilterOpen}
                onOpenChange={setIsFilterOpen}
                content={
                  isFilterOpen ? (
                    <div className="w-[42rem] rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t("ticketManagement.filterButton")}</h3>
                      </div>
                      <FormProvider {...filterMethods}>
                        <TicketFormFilter
                          filter={filterMethods.getValues()}
                          onFilterApply={handleFilterApply}
                          onCancel={() => setIsFilterOpen(false)}
                          showInactive={showInactive}
                          setShowInactive={setShowInactive}
                        />
                      </FormProvider>
                    </div>
                  ) : <div />
                }
              >
                <Button
                  color="primary"
                  className="inline-flex w-full items-center justify-center rounded-lg text-center text-sm font-medium text-white sm:w-auto"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <FaFilter
                    className="-ml-1 mr-2 h-4 w-4 text-current"
                    color="white"
                  />
                 {t("ticketManagement.filterButton")}
                </Button>
              </Popover>
              <Button
                color="gray"
                className="inline-flex items-center"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
                isProcessing={isDownloadingPdf}
              >
                <HiDownload className="mr-2 h-4 w-4" />
                {t("ticketManagement.downloadPdf")}
              </Button>
              </div>
              <TicketTable
                columns={columns}
                data={formattedData}
                pagination={data?.pagination}
                onPageChange={handlePaginationChange}
              />
            </div>
          )}
        </div>
      </Card>
      <FormProvider {...methods}>
        <TicketFormDialog
          open={isFormDialogOpen}
          onClose={handleFormDialogClose}
          mode="edit"
          onSubmit={handleUpdate}
          isLoading={isLoading}
          editFallbacks={editFallbacks}
        />
      </FormProvider>
      {confirmationDialog && (
        <ConfirmationDialog
          title={confirmationDialog.title}
          body={confirmationDialog.body}
          onCancel={confirmationDialog.onCancel}
          onConfirm={confirmationDialog.onConfirm}
          isLoading={confirmationDialog.isLoading}
        />
      )}
    </>
  );
}
